<template>
  <app-dialer-common-page
    class="app-dialer-main-page"
    :show-header="false"
    :is-loading="isLoading"
  >
    <app-dialer-call-card-info
      :agent="agent"
      :contact="contact"
      :avatar="avatar"
      :identifier="identifier"
      :members="members"
      :show-info-button="!!description"
      :is-info-button-active="isInfoButtonActive"
      size="mobile"
      @toggle-info="isInfoButtonActive = !isInfoButtonActive"
      @toggle-members="$emit('toggle-members-page')"
    />

    <app-dialer-call-status
      v-if="!isInfoButtonActive"
      :is-connecting="isConnecting"
      :is-in-progress="isInProgress"
      :timestamp="timestamp"
      :session-id="sessionId"
      size="mobile"
    />

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="isInfoButtonActive"
      class="info"
      v-html="descriptionText"
    />

    <app-dialer-call-controls
      :is-on-hold="isOnHold"
      :is-muted="isMuted"
      :is-put-on-hold-action-loading="isPutOnHoldActionLoading"
      :is-hang-up-action-loading="isHangUpActionLoading"
      :is-keypad-active="isKeypadActive"
      :is-invite-button-active="isInviteButtonActive"
      :is-transfer-button-active="isTransferButtonActive"
      :is-connecting="isConnecting"
      :has-keypad-button="hasKeypadButton"
      :has-invite-button="hasInviteButton"
      :has-transfer-button="hasTransferButton"
      :has-hold-button="hasHoldButton"
      :has-mute-button="hasMuteButton"
      :has-contact-member-on-call="hasContactMemberOnCall"
      size="mobile"
      @toggle-keypad="$emit('toggle-keypad')"
      @toggle-invite="$emit('toggle-invite-page')"
      @toggle-transfer="$emit('toggle-transfer-page')"
      @put-on-hold="$emit('put-on-hold')"
      @mute-self="$emit('mute-self')"
      @hang-up="$emit('hang-up')"
    />
  </app-dialer-common-page>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';

import { AppDialerCommonPage } from '../common';
import {
  AppDialerCallCardInfo,
  AppDialerCallStatus,
  AppDialerCallControls,
} from '../controls';
import { markdownToHTML } from '@/common';
import {
  AVATAR_PROP,
  AGENT_PROP,
  CONTACT_PROP,
  MEMBERS_PROP,
} from '../../constants';
import { CALL_MEMBER_STATUS, CALL_MEMBER_TYPE } from '../../types';

export default defineComponent({
  name: 'AppDialerMainPage',
  components: {
    AppDialerCommonPage,
    AppDialerCallCardInfo,
    AppDialerCallStatus,
    AppDialerCallControls,
  },
  props: {
    avatar: AVATAR_PROP,
    agent: AGENT_PROP,
    contact: CONTACT_PROP,
    identifier: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    members: MEMBERS_PROP,
    timestamp: {
      type: [String, Number],
      default: 0,
    },
    sessionId: {
      type: String,
      default: '',
    },
    isConnecting: Boolean,
    isInProgress: Boolean,
    isOnHold: Boolean,
    isMuted: Boolean,
    isPutOnHoldActionLoading: Boolean,
    isHangUpActionLoading: Boolean,
    isKeypadActive: Boolean,
    isInviteButtonActive: Boolean,
    isTransferButtonActive: Boolean,
    hasKeypadButton: Boolean,
    hasInviteButton: Boolean,
    hasTransferButton: Boolean,
    hasHoldButton: Boolean,
    hasMuteButton: Boolean,
  },
  emits: [
    'toggle-keypad',
    'toggle-members-page',
    'toggle-invite-page',
    'toggle-transfer-page',
    'put-on-hold',
    'mute-self',
    'hang-up',
  ],
  data() {
    return {
      isInfoButtonActive: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.isConnecting && !this.contact;
    },
    descriptionText(): string {
      return markdownToHTML(this.description);
    },
    hasContactMemberOnCall(): boolean {
      return this.members.some(member => member.type === CALL_MEMBER_TYPE.CONTACT && member.status === CALL_MEMBER_STATUS.JOINED);
    },
  },
});
</script>

<style lang="scss" scoped>
.app-dialer-main-page {
  display: flex;
  margin-top: 0 !important;

  .app-dialer-call-card-info {
    margin-bottom: 3rem;
  }

  .app-dialer-call-status {
    margin: auto 0;
  }

  .app-dialer-call-controls {
    margin: 3rem auto;
  }

  .info {
    flex: 1 1 100%;
    color: var(--or-c-inverse-on-surface);
    font-size: 1rem;
    line-height: 1.5;
    overflow-x: hidden;
    overflow-y: auto;
  }

  /* Override only Common Page progress color */
  ::v-deep .content > .or-progress .or-progress-pie.is-loading .half-circle {
    border-color: var(--or-c-inverse-on-surface) !important;
  }

  ::v-deep .content {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    background: var(--or-c-inverse-surface);
  }
}
</style>
